import React from "react";
import { View, Text, StyleSheet, ActivityIndicator } from "react-native";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import ShiftHandOverActivity from "./ShiftHandOverActivity";
import { getCategoryColor } from "../../utils/categoryColors";
import { useTheme } from "../../theme";

interface ShiftHandoverQuestionsProps {
  isVisible: boolean;
  forceDisabled?: boolean;
}

const ShiftHandoverQuestions: React.FC<ShiftHandoverQuestionsProps> = ({
  isVisible,
  forceDisabled = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const { shiftHandoverQuestions, loading } = useSelector(
    (state: RootState) => state.shiftHandOver
  );

  // Group questions by category
  const getQuestionsByCategory = () => {
    if (!shiftHandoverQuestions || shiftHandoverQuestions.length === 0) {
      return {};
    }

    return shiftHandoverQuestions.reduce(
      (acc: Record<string, any[]>, question: any) => {
        const category = question.category;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(question);
        return acc;
      },
      {}
    );
  };

  const questionsByCategory = getQuestionsByCategory();
  const categories = Object.keys(questionsByCategory);

  // Utility: Get header background color from categoryColors utility
  const getHeaderBackgroundColor = (category: string) => {
    // Using the shared utility function but preserving capitalization from original implementation
    return getCategoryColor(category);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <View style={styles.container}>
      {/* Show loading indicator while fetching questions */}
      {loading && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#0066CC" />
          <Text style={[styles.loadingText, isDarkMode && { color: colors.text.primary }]}>Loading questions...</Text>
        </View>
      )}

      {/* Display questions by category */}
      {!loading && categories.length > 0 && (
        <View style={styles.questionsContainer}>
          {categories.map((category) => (
            <ShiftHandOverActivity
              key={category}
              title={`${category}`}
              options={questionsByCategory[category]}
              category={category}
              headerBackgroundColor={getHeaderBackgroundColor(category)}
              forceDisabled={forceDisabled}
            />
          ))}
        </View>
      )}

      {/* Display message if no questions are available */}
      {!loading && categories.length === 0 && (
        <View
          style={[
            styles.noQuestionsContainer,
            isDarkMode && {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.default,
              borderWidth: 1,
            },
          ]}
        >
          <Text style={[styles.noQuestionsText, isDarkMode && { color: colors.text.primary }]}>
            No questions available. Please check your connection or try again
            later.
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 20,
  },
  loadingContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: "#0066CC",
  },
  questionsContainer: {
    //marginTop: 16,
  },
  noQuestionsContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8f9fa",
    borderRadius: 6,
    marginTop: 16,
  },
  noQuestionsText: {
    fontSize: 16,
    color: "#666",
    textAlign: "center",
  },
});

export default ShiftHandoverQuestions;
